import { splitRowLine } from './splits';
import { tipRowLine } from './tips';
import { fundSmallPrint } from '../../../shared/savings';
import type { OverviewDto, TransactionDto, TxSource } from '../../../shared/api';
import { dayLabel, dayOf, esc, money } from '../format';
import { catName, ui } from '../state';
import { reviewLabel } from './review';
import { topBar } from './header';
import { shareText } from './items';
import { reasonFor } from './newcard';
import { filedByHush } from '../../../shared/filed';
import { noteLine, searchText } from './notes';

const SOURCE_LABEL: Record<TxSource, string> = {
  email: 'Auto',
  manual: 'Manual',
  recurring: 'Logged for you',
  receipt: 'Receipt',
  transfer: 'Done',
};

/** The name a card shows ("Chase ...5411" or its nickname), from its last four digits. */
export const cardName = (d: Pick<OverviewDto, 'cards'>, last4: string | null) =>
  last4 ? (d.cards.find((k) => k.last4 === last4)?.label ?? `Card ...${last4}`) : null;

/** Logged by Hush itself (an emailed alert or an auto-log), so its row says why it was filed. */
export const autoFiled = (t: TransactionDto) => (t.filedBy ? filedByHush(t) : t.source === 'email' || t.source === 'recurring');

/**
 * One purchase row; tap to edit. `sub` replaces the category line (the category screen shows the
 * date). `card` adds a small card label (Activity, when the household has more than one card).
 * `why` (Activity, with the categories) adds a small dot on a purchase not looked at yet and, for one Hush filed by
 * itself, a grey line saying why.
 */
export function txButton(t: TransactionDto, sub?: string, card?: string | null, why: Pick<OverviewDto, 'categories'> | null = null): string {
  const base = sub ?? (t.categoryId ? catName(t.categoryId) : 'Needs a category');
  // A spread item's real charge: shown in full, counted as monthly shares.
  const line = [base, card, t.spread ? 'Spread over 12 months' : ''].filter(Boolean).join(' · ');
  const dot = why && !t.reviewed ? '<span class="newdot" role="img" aria-label="New"></span>' : '';
  const reason = why && autoFiled(t) && t.categoryId ? `<br><span class="why">${esc(reasonFor(why, t))}</span>` : '';
  return `<button class="tx" data-a="edit" data-id="${esc(t.id)}" data-q="${esc(searchText(t))}">
      <span><span class="tm">${dot}${esc(t.merchant)}</span>${t.note ? noteLine(t.note) : '<br>'}<span class="tc ${t.categoryId || sub ? '' : 'review'}">${esc(line)}</span>${reason}${t.split ? `<br>${splitRowLine(t)}` : t.tipCents ? `<br>${tipRowLine(t)}` : ''}${t.fundedFromCategoryId && catName(t.fundedFromCategoryId) ? `<br><span class="tsplit">${esc(fundSmallPrint(catName(t.fundedFromCategoryId)))}</span>` : ''}</span>
      <span class="ta">${money(t.countedCents, 2)}<span class="ts">${SOURCE_LABEL[t.source] ?? 'Auto'}</span></span></button>`;
}

/** A spread item's share: "Pliability, $15.00 of your yearly $180 (paid Mar 15, 2026). Month 5 of 12", tagged Prepaid. */
export function shareRow(l: OverviewDto['shareLines'][number]): string {
  return `<button class="tx share" data-a="item-edit" data-id="${esc(l.itemId)}">
      <span><span class="tm">${esc(shareText(l))}</span><br><span class="tc">${esc(catName(l.categoryId))}</span></span>
      <span class="ta">${money(l.shareCents, 2)}<span class="ts prepaid">Prepaid</span></span></button>`;
}

/** Activity's search: merchant, bank text or "What was this for?" (rows filter in place, main.ts filterActivity()). */
export const SEARCH_PLACEHOLDER = 'Search merchant or note';

export function vActivity(d: OverviewDto, search = ''): string {
  const today = d.month.today;
  const unc = d.transactions.filter((t) => !t.categoryId);
  const reviewCount = unc.length + d.reviewItems.length;
  const f = ui.filter;
  const chips = [`<button class="chip" data-a="filter" data-cat="all" aria-pressed="${f === 'all'}">All</button>`];
  // "New": purchases not looked at yet. No count; the rows carry a small dot.
  if (f === 'new' || d.transactions.some((t) => !t.reviewed))
    chips.push(`<button class="chip" data-a="filter" data-cat="new" aria-pressed="${f === 'new'}">New</button>`);
  if (reviewCount)
    chips.push(
      `<button class="chip" data-a="filter" data-cat="review" aria-pressed="${f === 'review'}">Review (${reviewCount})</button>`,
    );
  d.categories.forEach((k) =>
    chips.push(
      `<button class="chip" data-a="filter" data-cat="${esc(k.id)}" aria-pressed="${f === k.id}">${esc(k.name)}</button>`,
    ),
  );

  // With more than one card: a card filter, and each row says which card.
  const manyCards = d.cards.length > 1;
  const card = manyCards && d.cards.some((k) => k.id === ui.card) ? ui.card : 'all';
  const cardChips = manyCards
    ? `<div class="chips scroll cards">${[
        `<button class="chip" data-a="card-filter" data-card="all" aria-pressed="${card === 'all'}">All cards</button>`,
        ...d.cards.map((k) => `<button class="chip" data-a="card-filter" data-card="${esc(k.id)}" aria-pressed="${card === k.id}">${esc(k.label)}</button>`),
      ].join('')}</div>`
    : '';

  let list = d.transactions.filter((t) => dayOf(t.date) <= today);
  if (f === 'review') list = list.filter((t) => !t.categoryId);
  else if (f === 'new') list = list.filter((t) => !t.reviewed);
  else if (f !== 'all') list = list.filter((t) => t.categoryId === f);
  if (card !== 'all') {
    const last4 = d.cards.find((k) => k.id === card)!.last4;
    list = list.filter((t) => t.cardLast4 === last4);
  }
  list.sort((a, b) => b.date.localeCompare(a.date) || b.createdAt.localeCompare(a.createdAt));

  // Spread items' monthly shares: read-only lines on the 1st, under All and under their own
  // category; never under Review or a card filter (not a purchase). Tapping one opens its item.
  const shares = card === 'all' && f !== 'new' ? (d.shareLines ?? []).filter((l) => f === 'all' || f === l.categoryId) : [];
  const groups = new Map<string, string[]>();
  const add = (date: string, html: string) => groups.set(date, [...(groups.get(date) ?? []), html]);
  list.forEach((t) => add(t.date, txButton(t, undefined, manyCards ? cardName(d, t.cardLast4) : null, d)));
  shares.forEach((l) => add(l.date, shareRow(l)));
  const body = [...groups.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, rows]) => {
      const day = dayOf(date);
      const h = day === today ? 'Today' : day === today - 1 ? 'Yesterday' : dayLabel(date);
      return `<p class="day">${h}</p><div class="txg">${rows.join('')}</div>`;
    })
    .join('');
  const items =
    (f === 'review' || f === 'all') && d.reviewItems.length
      ? `<p class="day">Needs your help</p><div class="txg">${d.reviewItems
          .map(
            (r) => `<button class="tx" data-a="review-add" data-id="${esc(r.id)}">
      <span><span class="tm">${reviewLabel(r)}</span><br><span class="tc review">Tap to add the details</span></span>
      <span class="ta">${r.amountCents ? money(r.amountCents, 2) : ''}<span class="ts">${dayLabel(r.date)}</span></span></button>`,
          )
          .join('')}</div>`
      : '';
  const searchBox = list.length
    ? `<label class="fld"><span class="sr">Search</span><input id="act-search" type="search" placeholder="${SEARCH_PLACEHOLDER}" autocomplete="off" value="${esc(search)}"></label>`
    : '';
  return `${topBar('Activity', 'Tap any item to edit it')}
  <div class="chips scroll">${chips.join('')}</div>
  ${cardChips}
  ${searchBox}
  ${items}${body || (items ? '' : '<p class="empty">Nothing here yet. Tap + to log spending.</p>')}
  ${list.length ? '<p class="empty" data-search-none hidden>No purchases match.</p>' : ''}`;
}
